import { CheckIcon, XMarkIcon } from "@heroicons/react/20/solid";
import { type ReactNode, useMemo } from "react";
import { type Key, Menu as AriaMenu, MenuItem as AriaMenuItem, Separator as AriaSeparator, type Selection } from "react-aria-components";
import { cn } from "cn";
import { Button } from "@/components/ui/button.tsx";
import { Menu } from "@/components/ui/menu.tsx";
import { PopoverContent } from "@/components/ui/popover.tsx";
import { FilterChipButton } from "./FilterChip.tsx";

// intent-ui-deviation: raw react-aria Menu with a persistent checkbox column, not Intent MenuContent/MenuItem

const MENU_ITEM_CLASSNAME = ({ isFocused }: { isFocused: boolean }) => `hover:bg-secondary/60 col-span-full flex cursor-pointer items-center gap-3 rounded-md px-2.5 py-1.5 text-sm text-fg outline-none transition-colors ${isFocused ? "bg-accent" : ""}`;

const CLEAR_BUTTON_CLASSNAME = "text-muted-fg hover:text-fg hover:bg-accent flex h-auto !min-h-0 w-full items-center justify-start gap-3 rounded-md !px-2.5 !py-1.5 text-sm";

function FilterCheckbox({ isSelected }: { isSelected: boolean }) {
    return <span className={`border-border flex size-4 shrink-0 items-center justify-center rounded border transition-colors ${isSelected ? "border-primary bg-primary text-primary-fg" : "bg-bg"}`}>{isSelected && <CheckIcon className="size-3" aria-hidden="true" />}</span>;
}

export interface FilterChipMenuItem {
    id: string;
    label: string;
}

export function isSameSelection(selected: ReadonlySet<Key>, other: ReadonlySet<Key>): boolean {
    return selected.size === other.size && [...selected].every(key => other.has(key));
}

const HEADER_CLEAR_BUTTON_CLASSNAME = "text-fg hover:bg-secondary/60 flex h-auto !min-h-0 w-full items-center justify-start rounded-md !px-2.5 !py-1.5 text-sm font-medium";

export interface FilterChipMenuProps {
    label: string;
    icon?: ReactNode;
    getTriggerLabel?: (selectedCount: number) => string;
    menuAriaLabel: string;
    items: ReadonlyArray<FilterChipMenuItem>;
    selectedKeys: Set<Key>;
    defaultKeys?: ReadonlySet<Key>;
    onSelectionChange: (selection: Selection) => void;
    clearFilterLabel?: string;
    onClear?: () => void;
    clearPlacement?: "header" | "footer";
    popoverFooter?: ReactNode;
    className?: string;
    defaultOpen?: boolean;
    isOpen?: boolean;
    onOpenChange?: (isOpen: boolean) => void;
    hideClear?: boolean;
    dismissible?: boolean;
    dismissAriaLabel?: string;
    onDismiss?: () => void;
}

export function FilterChipMenu({
    label,
    icon,
    getTriggerLabel,
    menuAriaLabel,
    items,
    selectedKeys,
    defaultKeys,
    onSelectionChange,
    clearFilterLabel,
    onClear,
    clearPlacement = "footer",
    popoverFooter,
    className,
    defaultOpen,
    isOpen,
    onOpenChange,
    hideClear,
    dismissible,
    dismissAriaLabel,
    onDismiss
}: FilterChipMenuProps) {
    const activeCount = selectedKeys.size;
    const isActive = defaultKeys ? !isSameSelection(selectedKeys, defaultKeys) : activeCount > 0;
    const isDismissible = dismissible && !!onDismiss;
    const groupHoverAdd = isDismissible && !isActive ? "group-hover:border-muted-fg/40" : undefined;
    const triggerClassName = useMemo(() => cn(isDismissible && "rounded-r-none border-r-0 flex-1 min-w-0", groupHoverAdd, className), [isDismissible, groupHoverAdd, className]);
    const triggerLabel = getTriggerLabel ? getTriggerLabel(activeCount) : label;

    const menu = (
        <Menu defaultOpen={defaultOpen} isOpen={isOpen} onOpenChange={onOpenChange}>
            <FilterChipButton isActive={isActive} className={triggerClassName}>
                {icon && <span aria-hidden="true">{icon}</span>}
                <span className="min-w-0 truncate">{triggerLabel}</span>
                {!getTriggerLabel && isActive && <span className="bg-primary text-primary-fg min-h-4 min-w-4 rounded-full text-xs font-semibold tabular-nums">{activeCount}</span>}
            </FilterChipButton>
            <PopoverContent placement="bottom start">
                {isActive && !hideClear && clearPlacement === "header" && onClear && clearFilterLabel && (
                    <>
                        <div className="p-1.5">
                            <Button intent="plain" onPress={onClear} className={HEADER_CLEAR_BUTTON_CLASSNAME}>
                                {clearFilterLabel}
                            </Button>
                        </div>
                        <AriaSeparator className="border-border/40 h-px border-t" />
                    </>
                )}
                <AriaMenu aria-label={menuAriaLabel} selectionMode="multiple" selectedKeys={selectedKeys} onSelectionChange={onSelectionChange} className={cn("grid max-h-72 grid-cols-[auto_1fr] gap-y-1 overflow-y-auto overscroll-contain outline-none", items.length > 0 && "p-1.5")}>
                    {items.map(item => (
                        <AriaMenuItem key={item.id} id={item.id} textValue={item.label} className={MENU_ITEM_CLASSNAME}>
                            {({ isSelected }) => (
                                <>
                                    <FilterCheckbox isSelected={isSelected} />
                                    <span>{item.label}</span>
                                </>
                            )}
                        </AriaMenuItem>
                    ))}
                </AriaMenu>
                {!hideClear && clearPlacement === "footer" && onClear && clearFilterLabel && (
                    <>
                        {popoverFooter}
                        {isActive && (
                            <>
                                <AriaSeparator className="border-border/40 h-px border-t" />
                                <div className="p-1.5">
                                    <Button intent="plain" onPress={onClear} className={CLEAR_BUTTON_CLASSNAME}>
                                        <XMarkIcon aria-hidden="true" className="size-4" />
                                        {clearFilterLabel}
                                    </Button>
                                </div>
                            </>
                        )}
                    </>
                )}
            </PopoverContent>
        </Menu>
    );

    if (!dismissible || !onDismiss) {
        return menu;
    }

    return (
        <div className="group flex max-w-full min-w-0 items-center">
            {menu}
            <FilterChipButton isActive={isActive} onPress={onDismiss} aria-label={dismissAriaLabel} className={cn("rounded-l-none border-l-0 pr-2 pl-0 sm:pr-2 sm:pl-0", groupHoverAdd)}>
                <XMarkIcon aria-hidden="true" className="size-4" />
            </FilterChipButton>
        </div>
    );
}
